import React, { useState, useEffect, useRef } from 'react';
import {
  FaPlus, FaCalendarPlus, FaSave, FaChevronLeft, FaChevronRight,
  FaTrash, FaTimes, FaStickyNote, FaEye, FaCalendarAlt
} from 'react-icons/fa';
import './pfpastyears.css';
import PfManagementLayout from '../../Components/pfManagementLayout';
const PfPastYears = () => {
  const [yearlyData, setYearlyData] = useState({});
  const [currentYear, setCurrentYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnWidths, setColumnWidths] = useState({});
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false, rowId: null });
  const [showYearModal, setShowYearModal] = useState(false);
  const [newYearInput, setNewYearInput] = useState('');
  const [showNotesModal, setShowNotesModal] = useState({
    show: false, rowId: null, rowName: '', month: null, notes: '', contribution: ''
  });
  const [newColumnName, setNewColumnName] = useState('');
 
  const tableRef = useRef(null);
  const alertTimeoutRef = useRef(null);
  const modalRef = useRef(null);
  const showAlert = (message, type = 'success') => {
    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
   
    setAlert({ show: true, message, type });
   
    alertTimeoutRef.current = setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 4000);
  };
  // Initialize column widths
  useEffect(() => {
    const widths = {
      'Name': '250px',
      'Total': '120px',
      'Double': '120px',
      '20%': '100px',
      '80%': '100px',
      '50% Withdrawal': '120px',
      '50% Remaining': '120px',
      'Amount Withdraw': '140px',
      'Amount Remaining (80%)': '140px',
      'Actions': '100px',
    };
    ['Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May']
      .forEach(m => { widths[m] = '120px'; });
    setColumnWidths(widths);
  }, []);
  // Fetch available years
  useEffect(() => {
    let mounted = true;
    const fetchAvailableYears = async () => {
      setYearsLoading(true);
      try {
        const response = await fetch('https://backend.ems.hiradentallab.com/api/pfexcel/years');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (!result.success) throw new Error(result.message);
        const years = result.years || [];
        console.log('[YEARS] Loaded:', years);
        if (mounted) {
          const sortedYears = years.sort().reverse();
          setAvailableYears(sortedYears);
         
          if (sortedYears.length > 0 && !currentYear) {
            setCurrentYear(sortedYears[0]);
          }
        }
      } catch (err) {
        console.error('[YEARS] Failed:', err);
        showAlert('Failed to load years list', 'error');
      } finally {
        if (mounted) setYearsLoading(false);
      }
    };
    fetchAvailableYears();
    return () => { mounted = false; };
  }, []);
  // Fetch data for current year
  useEffect(() => {
    if (!currentYear?.trim() || yearsLoading) return;
    const fetchYearData = async () => {
      setDataLoading(true);
      try {
        const url = `https://backend.ems.hiradentallab.com/api/pfexcel/fetch/${encodeURIComponent(currentYear)}`;
        const res = await fetch(url);
        if (res.status === 404) {
          // Year doesn't exist yet, create empty data
          setYearlyData(prev => ({
            ...prev,
            [currentYear]: []
          }));
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        if (!result.success) throw new Error(result.message);
        console.log(`[FETCH] Data for ${currentYear}:`, result.data?.length || 0, 'rows');
       
        // Ensure monthDetails is properly structured
        const parsedData = (result.data || []).map(row => ({
          ...row,
          monthDetails: row.monthDetails || {},
          // Ensure all columns exist
          '50% Withdrawal': row['50% Withdrawal'] || '',
          '50% Remaining': row['50% Remaining'] || '',
          Name: row.Name || ''
        }));
        setYearlyData(prev => ({
          ...prev,
          [currentYear]: parsedData
        }));
      } catch (err) {
        console.error(`[FETCH] Failed for ${currentYear}:`, err);
        showAlert(`Failed to load ${currentYear} data`, 'error');
      } finally {
        setDataLoading(false);
      }
    };
    fetchYearData();
  }, [currentYear, yearsLoading]);
  // Modal click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowAddRowModal(false);
        setShowAddColumnModal(false);
        setShowDeleteModal({ show: false, rowId: null });
        setShowNotesModal({ show: false, rowId: null, rowName: '', month: null, notes: '', contribution: '' });
        setShowYearModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleCellChange = (id, column, value) => {
    setYearlyData(prev => ({
      ...prev,
      [currentYear]: (prev[currentYear] || []).map(row =>
        row.id === id ? { ...row, [column]: value } : row
      )
    }));
  };
  const handleCellClick = (rowIndex, column) => {
    setActiveCell(`${column}${rowIndex}`);
  };
  const openNotesModal = (rowId, rowName, month, currentContribution) => {
    const row = yearlyData[currentYear]?.find(r => r.id === rowId);
    const monthData = row?.monthDetails?.[month] || {};
    setShowNotesModal({
      show: true,
      rowId,
      rowName,
      month,
      notes: monthData.notes || '',
      contribution: currentContribution || monthData.contribution || ''
    });
  };
  const saveNotes = async () => {
    const { rowId, month, notes, contribution } = showNotesModal;
    if (!rowId || !month) return;
    const updated = (yearlyData[currentYear] || []).map(row => {
      if (row.id !== rowId) return row;
      const monthDetails = {
        ...(row.monthDetails || {}),
        [month]: {
          contribution: contribution || row[month] || '-',
          notes,
          lastUpdated: new Date().toISOString()
        }
      };
      return {
        ...row,
        [month]: contribution || row[month] || '-',
        monthDetails
      };
    });
    setYearlyData(prev => ({ ...prev, [currentYear]: updated }));
    setShowNotesModal({ show: false, rowId: null, rowName: '', month: null, notes: '', contribution: '' });
    // Auto-save after notes update
    await handleSave();
  };
  const addNewRow = () => {
    const rows = yearlyData[currentYear] || [];
    const maxId = rows.length > 0
      ? Math.max(...rows.map(r => r.id || 0))
      : 0;
    const newRow = {
      id: maxId + 1,
      Name: `Employee ${maxId + 1}`,
      Total: '',
      Double: '',
      '20%': '',
      '80%': '',
      '50% Withdrawal': '',
      '50% Remaining': '',
      'Amount Withdraw': '',
      'Amount Remaining (80%)': '',
      Jun: '-', Jul: '-', Aug: '-', Sep: '-', Oct: '-', Nov: '-',
      Dec: '-', Jan: '-', Feb: '-', Mar: '-', Apr: '-', May: '-',
      monthDetails: {}
    };
    setYearlyData(prev => ({
      ...prev,
      [currentYear]: [...rows, newRow]
    }));
    setShowAddRowModal(false);
    showAlert('Row added successfully', 'success');
  };
  const confirmDeleteRow = async () => {
    const { rowId } = showDeleteModal;
    if (!rowId) return;
    const updated = (yearlyData[currentYear] || []).filter(r => r.id !== rowId);
    setYearlyData(prev => ({ ...prev, [currentYear]: updated }));
    setShowDeleteModal({ show: false, rowId: null });
    await handleSave();
    showAlert('Row deleted successfully', 'success');
  };
  const addNewColumn = () => {
    if (!newColumnName.trim()) {
      showAlert('Please enter a column name', 'error');
      return;
    }
    const updated = (yearlyData[currentYear] || []).map(row => ({
      ...row,
      [newColumnName]: '-'
    }));
    setYearlyData(prev => ({ ...prev, [currentYear]: updated }));
    setNewColumnName('');
    setShowAddColumnModal(false);
    showAlert(`Column "${newColumnName}" added`, 'success');
  };
  const deleteColumn = async (column) => {
    if (!window.confirm(`Delete column "${column}"? This cannot be undone.`)) return;
    const updated = (yearlyData[currentYear] || []).map(row => {
      const { [column]: _, monthDetails = {}, ...rest } = row;
      const newMonthDetails = { ...monthDetails };
      delete newMonthDetails[column];
      return { ...rest, monthDetails: newMonthDetails };
    });
    setYearlyData(prev => ({ ...prev, [currentYear]: updated }));
    await handleSave();
    showAlert(`Column "${column}" deleted`, 'success');
  };
  const changeYear = (direction) => {
    if (availableYears.length === 0) return;
    const idx = availableYears.indexOf(currentYear);
    if (direction === 'prev' && idx < availableYears.length - 1) {
      setCurrentYear(availableYears[idx + 1]);
    } else if (direction === 'next' && idx > 0) {
      setCurrentYear(availableYears[idx - 1]);
    }
  };
  const handleSave = async () => {
    if (!currentYear?.trim()) {
      showAlert('No year selected', 'error');
      return;
    }
    const rows = yearlyData[currentYear] || [];
    if (rows.length === 0) {
      showAlert('Table is empty — nothing to save', 'warning');
      return;
    }
    // Prepare payload
    const payload = rows.map(row => ({
      ...row,
      monthDetails: row.monthDetails || {}
    }));
    try {
      // Check if year exists
      const checkUrl = `https://backend.ems.hiradentallab.com/api/pfexcel/fetch/${encodeURIComponent(currentYear)}`;
      const checkRes = await fetch(checkUrl);
      let res;
      let successMsg = '';
      if (checkRes.ok) {
        // Update existing
        res = await fetch(`https://backend.ems.hiradentallab.com/api/pfexcel/update/${encodeURIComponent(currentYear)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload })
        });
        successMsg = 'Data updated';
      } else {
        // Create new
        res = await fetch('https://backend.ems.hiradentallab.com/api/pfexcel/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            year: currentYear,
            data: payload
          })
        });
        successMsg = 'Data saved';
      }
      const result = await res.json();
     
      if (!result.success) {
        throw new Error(result.message || `Server returned ${res.status}`);
      }
      showAlert(`${successMsg} successfully!`, 'success');
     
      // Refresh available years list
      const yearsRes = await fetch('https://backend.ems.hiradentallab.com/api/pfexcel/years');
      const yearsData = await yearsRes.json();
      if (yearsData.success) {
        setAvailableYears(yearsData.years.sort().reverse());
      }
     
    } catch (err) {
      console.error('[SAVE ERROR]', err);
      showAlert(`Save failed: ${err.message}`, 'error');
    }
  };
  const addNewYear = async () => {
    if (!newYearInput.trim() || !/^\d{4}-\d{4}$/.test(newYearInput)) {
      showAlert('Please enter a valid year in format YYYY-YYYY', 'error');
      return;
    }
    if (availableYears.includes(newYearInput)) {
      showAlert('Year already exists', 'error');
      return;
    }
    try {
      // Create empty year data
      const initialData = [{
        id: 1,
        Name: 'Sample Employee',
        Total: '0',
        Double: '0',
        '20%': '0',
        '80%': '0',
        '50% Withdrawal': '0',
        '50% Remaining': '0',
        'Amount Withdraw': '0',
        'Amount Remaining (80%)': '0',
        Jun: '-', Jul: '-', Aug: '-', Sep: '-', Oct: '-', Nov: '-',
        Dec: '-', Jan: '-', Feb: '-', Mar: '-', Apr: '-', May: '-',
        monthDetails: {}
      }];
      const res = await fetch('https://backend.ems.hiradentallab.com/api/pfexcel/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: newYearInput,
          data: initialData
        })
      });
      const result = await res.json();
     
      if (!result.success) {
        throw new Error(result.message);
      }
      // Add to available years
      const newYears = [...availableYears, newYearInput].sort().reverse();
      setAvailableYears(newYears);
      setCurrentYear(newYearInput);
      setNewYearInput('');
      setShowYearModal(false);
     
      showAlert(`Year ${newYearInput} created successfully`, 'success');
     
    } catch (err) {
      console.error('[ADD YEAR ERROR]', err);
      showAlert(`Failed to add year: ${err.message}`, 'error');
    }
  };
  const filteredData = (yearlyData[currentYear] || [])
    .filter(row => row.Name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const [startYear, endYear] = currentYear ? currentYear.split('-') : ['', ''];
  const periodText = currentYear ? `1 Jun ${startYear} - 31 May ${endYear}` : '';
  const columns = [
    'Name', 'Total', 'Double', '20%', '80%',
    '50% Withdrawal', '50% Remaining', // Added 50% columns
    'Amount Withdraw', 'Amount Remaining (80%)',
    'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    'Jan', 'Feb', 'Mar', 'Apr', 'May'
  ];
  const parseRs = (str) => {
    const num = parseFloat(String(str || '0').replace(/[^0-9.]/g, '')) || 0;
    return isNaN(num) ? 0 : num;
  };
  const totals = {
    Total: filteredData.reduce((sum, r) => sum + parseRs(r.Total), 0).toLocaleString() + ' Rs',
    Double: filteredData.reduce((sum, r) => sum + parseRs(r.Double), 0).toLocaleString() + ' Rs',
    '20%': filteredData.reduce((sum, r) => sum + parseRs(r['20%']), 0).toLocaleString() + ' Rs',
    '80%': filteredData.reduce((sum, r) => sum + parseRs(r['80%']), 0).toLocaleString() + ' Rs',
    '50% Withdrawal': filteredData.reduce((sum, r) => sum + parseRs(r['50% Withdrawal']), 0).toLocaleString() + ' Rs',
    '50% Remaining': filteredData.reduce((sum, r) => sum + parseRs(r['50% Remaining']), 0).toLocaleString() + ' Rs',
    'Amount Withdraw': filteredData.reduce((sum, r) => sum + parseRs(r['Amount Withdraw']), 0).toLocaleString() + ' Rs',
    'Amount Remaining (80%)': filteredData.reduce((sum, r) => sum + parseRs(r['Amount Remaining (80%)']), 0).toLocaleString() + ' Rs',
  };
  const getNotesCount = (md = {}) =>
    Object.values(md).filter(v => v?.notes?.trim()).length;
  const totalNotes = (yearlyData[currentYear] || [])
    .reduce((sum, row) => sum + getNotesCount(row.monthDetails), 0);
  return (
    <PfManagementLayout>
      <div className="pf-container">
        {/* Alert */}
        {alert.show && (
          <div className={`pf-alert pf-alert-${alert.type}`}>
            <div className="pf-alert-content">
              <div className="pf-alert-icon">{alert.type === 'success' ? '✓' : '⚠'}</div>
              <div className="pf-alert-message">{alert.message}</div>
            </div>
            <div className="pf-alert-progress"></div>
          </div>
        )}
        {/* Add Year Modal */}
        {showYearModal && (
          <div className="pf-modal-overlay">
            <div className="pf-modal" ref={modalRef}>
              <div className="pf-modal-header">
                <h2>Add New Year</h2>
                <button className="pf-modal-close" onClick={() => setShowYearModal(false)}><FaTimes /></button>
              </div>
              <div className="pf-modal-body">
                <label>Year (Format: YYYY-YYYY)</label>
                <input
                  value={newYearInput}
                  onChange={e => setNewYearInput(e.target.value)}
                  placeholder="e.g., 2024-2025"
                  autoFocus
                />
                <p className="pf-modal-hint">Enter financial year from June to May</p>
              </div>
              <div className="pf-modal-footer">
                <button className="pf-modal-cancel" onClick={() => setShowYearModal(false)}>Cancel</button>
                <button className="pf-modal-confirm" onClick={addNewYear}>Add Year</button>
              </div>
            </div>
          </div>
        )}
        {/* Add Row Modal */}
        {showAddRowModal && (
          <div className="pf-modal-overlay">
            <div className="pf-modal" ref={modalRef}>
              <div className="pf-modal-header">
                <h2>Add New Row</h2>
                <button className="pf-modal-close" onClick={() => setShowAddRowModal(false)}><FaTimes /></button>
              </div>
              <div className="pf-modal-body">
                <p>Add a new employee row?</p>
              </div>
              <div className="pf-modal-footer">
                <button className="pf-modal-cancel" onClick={() => setShowAddRowModal(false)}>Cancel</button>
                <button className="pf-modal-confirm" onClick={addNewRow}>Add Row</button>
              </div>
            </div>
          </div>
        )}
        {/* Add Column Modal */}
        {showAddColumnModal && (
          <div className="pf-modal-overlay">
            <div className="pf-modal" ref={modalRef}>
              <div className="pf-modal-header">
                <h2>Add New Column</h2>
                <button className="pf-modal-close" onClick={() => setShowAddColumnModal(false)}><FaTimes /></button>
              </div>
              <div className="pf-modal-body">
                <label>Column Name</label>
                <input
                  value={newColumnName}
                  onChange={e => setNewColumnName(e.target.value)}
                  placeholder="e.g. Department"
                  autoFocus
                />
              </div>
              <div className="pf-modal-footer">
                <button className="pf-modal-cancel" onClick={() => setShowAddColumnModal(false)}>Cancel</button>
                <button className="pf-modal-confirm" onClick={addNewColumn}>Add Column</button>
              </div>
            </div>
          </div>
        )}
        {/* Delete Row Modal */}
        {showDeleteModal.show && (
          <div className="pf-modal-overlay">
            <div className="pf-modal" ref={modalRef}>
              <div className="pf-modal-header pf-modal-header-delete">
                <h2>Confirm Delete</h2>
                <button className="pf-modal-close" onClick={() => setShowDeleteModal({ show: false, rowId: null })}><FaTimes /></button>
              </div>
              <div className="pf-modal-body">
                <p>Delete this row permanently?</p>
              </div>
              <div className="pf-modal-footer">
                <button className="pf-modal-cancel" onClick={() => setShowDeleteModal({ show: false, rowId: null })}>Cancel</button>
                <button className="pf-modal-delete" onClick={confirmDeleteRow}>Delete</button>
              </div>
            </div>
          </div>
        )}
        {/* Notes Modal */}
        {showNotesModal.show && (
          <div className="pf-modal-overlay">
            <div className="pf-modal pf-month-modal" ref={modalRef}>
              <div className="pf-modal-header pf-modal-header-notes">
                <h2>
                  <FaStickyNote /> {showNotesModal.month} – {showNotesModal.rowName}
                </h2>
                <button className="pf-modal-close" onClick={() => setShowNotesModal({ show: false, rowId: null, rowName: '', month: null, notes: '', contribution: '' })}><FaTimes /></button>
              </div>
              <div className="pf-modal-body">
                <div className="pf-month-input-group">
                  <label>Contribution Amount</label>
                  <input
                    value={showNotesModal.contribution}
                    onChange={e => setShowNotesModal(p => ({ ...p, contribution: e.target.value }))}
                    placeholder="e.g. 12000"
                  />
                </div>
                <div className="pf-month-input-group">
                  <label>Notes / Withdrawal Details</label>
                  <textarea
                    value={showNotesModal.notes}
                    onChange={e => setShowNotesModal(p => ({ ...p, notes: e.target.value }))}
                    rows={4}
                    placeholder="Medical emergency, education fee, etc..."
                  />
                </div>
              </div>
              <div className="pf-modal-footer">
                <button className="pf-modal-cancel" onClick={() => setShowNotesModal({ show: false, rowId: null, rowName: '', month: null, notes: '', contribution: '' })}>Cancel</button>
                <button className="pf-modal-confirm" onClick={saveNotes}><FaSave /> Save</button>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="pf-header">
          <h1 style={{ color: "#0353a4", margin: "0" }}>Previous Year Reports</h1>
          <div className="pf-header-actions">
            <button
              className="pf-add-year-btn"
              onClick={() => setShowYearModal(true)}
            >
              <FaCalendarAlt /> Add New Year
            </button>
          </div>
        </div>
        <div className="pf-period">{periodText}</div>
        {totalNotes > 0 && (
          <div className="pf-notes-summary">Total notes: {totalNotes}</div>
        )}
        <hr />
        <div className='pf-container-excel'>
          {/* Top Bar */}
          <div className="pf-top-bar">
            <div className="pf-year-selector">
              <button
                className="pf-year-chevron"
                onClick={() => changeYear('prev')}
                disabled={yearsLoading || availableYears.indexOf(currentYear) >= availableYears.length - 1}
              >
                <FaChevronLeft />
              </button>
              <div className="pf-year">
                {yearsLoading ? 'Loading...' : (currentYear || 'Select Year')}
                {dataLoading && <span className="pf-loading-indicator"> (Loading...)</span>}
              </div>
              <button
                className="pf-year-chevron"
                onClick={() => changeYear('next')}
                disabled={yearsLoading || availableYears.indexOf(currentYear) <= 0}
              >
                <FaChevronRight />
              </button>
            </div>
            <div className="pf-search">
              <input
                placeholder="Search by name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {/* Table Grid */}
          <div className="pf-grid-container" ref={tableRef}>
            <div className="pf-grid">
              {/* Header Row */}
              <div className="pf-row header">
                {columns.map(col => (
                  <div
                    key={col}
                    className="pf-cell"
                    style={{ width: columnWidths[col], minWidth: columnWidths[col] }}
                  >
                    {col}
                    {col !== 'Name' && !['Total','Double','20%','80%','50% Withdrawal','50% Remaining','Amount Withdraw','Amount Remaining (80%)'].includes(col) && (
                      <button
                        className="pf-column-delete"
                        onClick={() => deleteColumn(col)}
                        title="Delete column"
                      >×</button>
                    )}
                  </div>
                ))}
                <div className="pf-cell" style={{ width: '100px', minWidth: '100px' }}>Actions</div>
              </div>
              {/* Data Rows */}
              {filteredData.map((row, rowIndex) => (
                <div key={row.id} className="pf-row">
                  {columns.map(col => {
                    if (['Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May'].includes(col)) {
                      const hasNotes = !!row.monthDetails?.[col]?.notes?.trim();
                      return (
                        <div
                          key={col}
                          className={`pf-cell month-cell ${activeCell === `${col}${rowIndex}` ? 'active' : ''} ${hasNotes ? 'has-notes' : ''}`}
                          style={{ width: '120px', minWidth: '120px' }}
                        >
                          <div className="pf-month-cell-content">
                            <input
                              value={row[col] || '-'}
                              onChange={e => handleCellChange(row.id, col, e.target.value)}
                              onFocus={() => handleCellClick(rowIndex, col)}
                              className="pf-month-input"
                            />
                            <button
                              className="pf-month-notes-btn"
                              onClick={() => openNotesModal(row.id, row.Name, col, row[col])}
                              title={hasNotes ? "Edit notes" : "Add notes"}
                              data-has-notes={hasNotes ? "true" : "false"}
                            >
                              {hasNotes ? <FaEye /> : <FaStickyNote />}
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={col}
                        className={`pf-cell ${activeCell === `${col}${rowIndex}` ? 'active' : ''}`}
                        onClick={() => handleCellClick(rowIndex, col)}
                        style={{ width: columnWidths[col], minWidth: columnWidths[col] }}
                      >
                        <input
                          value={row[col] || ''}
                          onChange={e => handleCellChange(row.id, col, e.target.value)}
                        />
                      </div>
                    );
                  })}
                  {/* Actions Cell */}
                  <div className="pf-cell" style={{ width: '100px', minWidth: '100px' }}>
                    <button
                      className="pf-delete-icon"
                      onClick={() => setShowDeleteModal({ show: true, rowId: row.id })}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
              {/* Empty State */}
              {filteredData.length === 0 ? (
                <div className="pf-row empty-row">
                  <div className="pf-cell" style={{ gridColumn: '1 / -1' }}>
                    {yearlyData[currentYear]?.length === 0
                      ? "No data for this year. Add rows to start."
                      : "No matching records found."}
                  </div>
                </div>
              ) : (
                /* Totals Row */
                <div className="pf-row total">
                  <div className="pf-cell" style={{ width: '250px', minWidth: '250px' }}>Total</div>
                  <div className="pf-cell" style={{ width: '120px' }}>{totals.Total}</div>
                  <div className="pf-cell" style={{ width: '120px' }}>{totals.Double}</div>
                  <div className="pf-cell" style={{ width: '100px' }}>{totals['20%']}</div>
                  <div className="pf-cell" style={{ width: '100px' }}>{totals['80%']}</div>
                  <div className="pf-cell" style={{ width: '120px' }}>{totals['50% Withdrawal']}</div>
                  <div className="pf-cell" style={{ width: '120px' }}>{totals['50% Remaining']}</div>
                  <div className="pf-cell" style={{ width: '140px' }}>{totals['Amount Withdraw']}</div>
                  <div className="pf-cell" style={{ width: '140px' }}>{totals['Amount Remaining (80%)']}</div>
                  {['Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May'].map(m => (
                    <div key={m} className="pf-cell" style={{ width: '120px' }}>-</div>
                  ))}
                  <div className="pf-cell" style={{ width: '100px' }}></div>
                </div>
              )}
            </div>
          </div>
          {/* Footer */}
          <div className="pf-footer">
            <div>
              Showing {filteredData.length} of {yearlyData[currentYear]?.length || 0} entries
              {totalNotes > 0 && ` • ${totalNotes} notes`}
            </div>
            <div className="pf-footer-buttons">
              <button
                className="pf-icon-button"
                onClick={() => setShowAddRowModal(true)}
                title="Add Row"
                disabled={!currentYear}
              >
                <FaPlus />
              </button>
              <button
                className="pf-icon-button"
                onClick={() => setShowAddColumnModal(true)}
                title="Add Column"
                disabled={!currentYear}
              >
                <FaCalendarPlus />
              </button>
              <button
                className="pf-icon-button save-button"
                onClick={handleSave}
                disabled={yearsLoading || !currentYear || dataLoading}
                title="Save All Data"
              >
                <FaSave />
              </button>
            </div>
          </div>
        </div>
      </div>
    </PfManagementLayout>
  );
};
export default PfPastYears;